import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DirectionIcon from "./../../media/direction-icon.svg";
import DirectionIconOrange from "./../../media/orange-sort-icon.svg";
import ButtonDelete from "./../../media/delete-button.svg";
import CreateMyTODO from "./createMyTODO";
import httpClient from "../../services/httpClient";
import { useNotification } from "../NotificationContext";
import "../../styleSheet/main/components/TODO/createMyTODO/createMyTODO.css";

import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { pl } from "date-fns/locale";

const MyTODO = () => {
  const { t } = useTranslation();
  const [hover, setHover] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskStatuses, setTaskStatuses] = useState({});
  const [tasks, setTasks] = useState([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [_loading, setLoading] = useState(true);
  const [_error, setError] = useState(null);
  // const { showNotification } = useNotification();

  const [showHistory, setShowHistory] = useState(false);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [newTaskDate, setNewTaskDate] = useState(null);

  const [editedText, setEditedText] = useState("");
  const [editedComment, setEditedComment] = useState(""); // Nowy stan na komentarz
  const [editedStatus, setEditedStatus] = useState(""); // Nowy stan na tymczasowy status

  // Function to fetch tasks from backend
  const fetchTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await httpClient.get("/ToDoTasks/assigned-to-myself", {
        withAuth: true,
      });

      // API returns: id, title, comment, dueDate, status, completedAtUtc, createdAt, assignedBy, assignedTo
      const fetchedTasks = response.data || [];

      // Mapowanie statusów z angielskiego na polski
      const statusMapping = {
        NotStarted: "",
        Started: "Zaczęte",
        InProgress: "W trakcie",
        Completed: "Skończone",
      };

      // Filter out completed tasks from main TODO list
      const nonCompletedTasks = fetchedTasks.filter((task) => {
        const status = task.status || "NotStarted";
        return status !== "Completed";
      });

      // Transform tasks to match current structure
      const transformedTasks = nonCompletedTasks.map((task) => ({
        text: task.title,
        date: task.dueDate,
        comment: task.comment || "",
        id: task.id,
        assignedBy: task.assignedBy,
        assignedTo: task.assignedTo,
      }));

      setTasks(transformedTasks);

      // Set task statuses based on the status from backend with proper mapping (only for non-completed tasks)
      const statuses = {};
      nonCompletedTasks.forEach((task) => {
        const taskKey = task.title;
        const backendStatus = task.status || "NotStarted";
        const mappedStatus = statusMapping[backendStatus] || "";
        statuses[taskKey] = mappedStatus;
      });
      setTaskStatuses(statuses);
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setError("Nie udało się pobrać zadań z serwera");
      // Fallback to localStorage if backend fails
      const savedStatuses =
        JSON.parse(localStorage.getItem("taskStatuses")) || {};
      const savedTasks = JSON.parse(localStorage.getItem("tasks")) || [];
      setTaskStatuses(savedStatuses);
      setTasks(savedTasks);
    } finally {
      setLoading(false);
    }
  };

  // Function to fetch completed tasks history from backend
  const fetchTaskHistory = async () => {
    try {
      const response = await httpClient.get(
        "/ToDoTasks/assigned-to-myself/history",
        {
          withAuth: true,
        },
      );

      // API returns: id, title, completedAt
      const historyTasks = response.data || [];

      // Transform history tasks to match current structure
      const transformedHistoryTasks = historyTasks.map((task) => ({
        text: task.title,
        date: task.completedAt,
        comment: "",
        id: task.id,
        completedDate: task.completedAt,
      }));

      setCompletedTasks(transformedHistoryTasks);
    } catch (err) {
      console.error("Error fetching task history:", err);
      // Fallback to localStorage if backend fails
      const savedCompletedTasks =
        JSON.parse(localStorage.getItem("completedTasks")) || [];
      setCompletedTasks(savedCompletedTasks);
    }
  };

  useEffect(() => {
    // Fetch tasks from backend on component mount
    fetchTasks();

    // Fetch completed tasks history from backend
    fetchTaskHistory();
  }, []);

  useEffect(() => {
    // Save to localStorage as backup
    localStorage.setItem("taskStatuses", JSON.stringify(taskStatuses));
    localStorage.setItem("tasks", JSON.stringify(tasks));
    localStorage.setItem("completedTasks", JSON.stringify(completedTasks));
  }, [taskStatuses, tasks, completedTasks]);

  const getDateLabel = (taskDate) => {
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(today.getDate() + 1);
    const taskDateObj = new Date(taskDate);

    if (taskDateObj < today.setHours(0, 0, 0, 0)) {
      return {
        label: taskDateObj.toLocaleDateString("pl-PL", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        isPast: true,
      };
    } else if (taskDateObj.toDateString() === today.toDateString()) {
      return { label: "na dzisiaj", isPast: false };
    } else if (taskDateObj.toDateString() === tomorrow.toDateString()) {
      return { label: "na jutro", isPast: false };
    } else {
      return {
        label: taskDateObj.toLocaleDateString("pl-PL", {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
        }),
        isPast: false,
      };
    }
  };

  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setEditedText(task.text);
    setNewTaskDate(new Date(task.date));
    setEditedComment(task.comment || ""); // Pobranie istniejącego komentarza lub ustawienie pustego
    setEditedStatus(taskStatuses[task.text] || ""); // Ustaw aktualny status zadania
  };

  const closeModal = () => {
    setSelectedTask(null);
  };

  const handleSaveChanges = async () => {
    try {
      // Mapowanie statusów z polskiego na angielski dla API
      const statusMapping = {
        Zaczęte: "Started",
        "W trakcie": "InProgress",
        Skończone: "Completed",
        "": "NotStarted",
      };

      // This component only handles tasks assigned for myself by myself
      // Always use the /for-myself endpoint
      const forMyselfUpdateData = {
        title: editedText,
        dueDate: newTaskDate.toISOString().split("T")[0],
        comment: editedComment || "",
        status: statusMapping[editedStatus] || "NotStarted",
      };

      await httpClient.put(
        `/ToDoTasks/${selectedTask.id}/for-myself`,
        forMyselfUpdateData,
        { withAuth: true },
      );

      // Powiadom o sukcesie
      // showNotification("Zadanie zostało zaktualizowane pomyślnie!", "success");

      setSelectedTask(null); // Zamknij modal po zapisaniu

      // Odśwież listę zadań z backendu po edycji
      fetchTasks();

      // Odśwież historię zadań, na wypadek gdyby zadanie zostało ukończone
      fetchTaskHistory();
    } catch (error) {
      console.error("Błąd podczas aktualizacji zadania:", error);
      // Powiadom o błędzie
      // showNotification(
      //   `Błąd podczas aktualizacji zadania: ${error.message}`,
      //   "error"
      // );
    }
  };

  const handleStatusChange = (status) => {
    setEditedStatus(status); // Tylko ustaw tymczasowy status, nie zapisuj od razu
  };

  // Use tasks directly as they are already filtered to exclude completed tasks in fetchTasks()
  const activeTasks = tasks;

  const groupedTasks = activeTasks.reduce((acc, task) => {
    const { label, isPast } = getDateLabel(task.date);
    if (!acc[label]) {
      acc[label] = [];
    }
    acc[label].push({ ...task, isPast });
    return acc;
  }, {});

  const sortedLabels = [
    ...Object.keys(groupedTasks)
      .filter((label) => groupedTasks[label][0]?.isPast)
      .sort((a, b) => new Date(a) - new Date(b)), // Przeszłe na czerwono
    "na dzisiaj",
    "na jutro",
    ...Object.keys(groupedTasks)
      .filter(
        (label) =>
          !["na dzisiaj", "na jutro"].includes(label) &&
          !groupedTasks[label][0]?.isPast,
      )
      .sort((a, b) => new Date(a) - new Date(b)), // Przyszłe
  ];

  return (
    <div className="mytodo-content">
      {!showHistory ? (
        <>
          <p>{t("todo.myListTitle")}</p>
          <div className="tasks-container">
            {tasks.length === 0 ? (
              <div className="no-tasks">
                <p className="no-tasks-message">{t("todo.noTasksAssigned")}</p>
              </div>
            ) : (
              sortedLabels.map((label, index) => {
                if (!groupedTasks[label]?.length) return null;
                return (
                  <div key={index}>
                    <strong
                      style={{
                        color: groupedTasks[label][0]?.isPast
                          ? "red"
                          : "inherit",
                      }}
                    >
                      {label}:
                    </strong>
                    {groupedTasks[label]?.map((task, taskIndex) => (
                      <div
                        key={taskIndex}
                        className={`task ${task.isPast ? "overdue" : ""}`}
                        onClick={() => handleTaskClick(task)}
                      >
                        <div
                          className={`square ${
                            taskStatuses[task.text] === "Skończone"
                              ? "completed"
                              : taskStatuses[task.text] === "W trakcie"
                                ? "in-progress"
                                : taskStatuses[task.text] === "Zaczęte"
                                  ? "started"
                                  : ""
                          }`}
                        ></div>
                        <span
                          className={
                            taskStatuses[task.text] === "Skończone"
                              ? "strikethrough"
                              : ""
                          }
                        >
                          {task.text}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })
            )}
          </div>

          <div className="footer-section">
            <div className="header-buttons">
              <button
                className="booking-button"
                onMouseEnter={() => setHover(true)}
                onMouseLeave={() => setHover(false)}
                onClick={() => setShowHistory(true)}
              >
                <div
                  className="icon"
                  style={{
                    backgroundImage: `url(${
                      hover ? DirectionIconOrange : DirectionIcon
                    })`,
                    transform: hover ? "scale(1.2)" : "rotate(270deg)",
                  }}
                ></div>
                {t("todo.history")}
              </button>
            </div>
            <div className="creating">
              <button
                className="create-button"
                onClick={() => setShowCreateForm(true)}
              >
                {t("todo.createNewForSelf")}
              </button>
            </div>
          </div>

          {showCreateForm && (
            <CreateMyTODO
              onClose={() => setShowCreateForm(false)}
              onCreate={() => {
                // Refresh tasks from backend after creating a new task
                fetchTasks();
                // Modal zostanie zamknięty automatycznie przez CreateMyTODO po udanym wysłaniu
              }}
            />
          )}

          {selectedTask && (
            <div className="modal-overlay">
              <div className="modal-content-TODO">
                <img
                  src={ButtonDelete}
                  className="close-button-TODO"
                  onClick={closeModal}
                  alt="Close"
                />
                <p>{t("todo.selectStage")}</p>

                {/* Edytuj treść zadania */}
                <div className="form-group">
                  <div className="left">
                    <div className="think">{t("todo.content")}</div>
                  </div>
                  <input
                    type="text"
                    value={editedText}
                    onChange={(e) => setEditedText(e.target.value)}
                  />
                </div>

                {/* Edytuj datę zadania przy użyciu DatePicker */}
                <div className="form-group">
                  <div className="left">
                    <div className="think">{t("todo.date")}</div>
                  </div>
                  <DatePicker
                    selected={newTaskDate}
                    onChange={(date) => setNewTaskDate(date)} // Zaktualizowano z `setEditedDate` na `setNewTaskDate`
                    locale={pl}
                    dateFormat="dd/MM/yyyy"
                    className="custom-datepicker"
                    minDate={new Date()}
                  />
                </div>

                {/* Nowa sekcja: Komentarz */}
                <div className="form-group">
                  <div className="left">
                    <div className="think">{t("todo.comment")}</div>
                  </div>
                  <textarea
                    value={editedComment} // Komentarz powiązany ze stanem
                    onChange={(e) => setEditedComment(e.target.value)}
                    rows="4"
                    placeholder={t("todo.addCommentPlaceholder")}
                  />
                </div>

                {/* Opcje statusu */}
                <div className="status-options">
                  <div
                    className="status-option"
                    onClick={() => handleStatusChange("Zaczęte")}
                  >
                    <div
                      className={`square ${
                        editedStatus === "Zaczęte" ? "started" : ""
                      }`}
                    ></div>
                    <span>{t("todo.status.started")}</span>
                  </div>
                  <div
                    className="status-option"
                    onClick={() => handleStatusChange("W trakcie")}
                  >
                    <div
                      className={`square ${
                        editedStatus === "W trakcie" ? "in-progress" : ""
                      }`}
                    ></div>
                    <span>{t("todo.status.inProgress")}</span>
                  </div>
                  <div
                    className="status-option"
                    onClick={() => handleStatusChange("Skończone")}
                  >
                    <div
                      className={`square ${
                        editedStatus === "Skończone" ? "completed" : ""
                      }`}
                    ></div>
                    <span>{t("todo.status.completed")}</span>
                  </div>
                </div>

                {/* Wyśrodkowane przyciski zapisu i anulowania */}
                <div className="modal-buttons">
                  <button className="save-button" onClick={handleSaveChanges}>
                    {t("todo.saveChanges")}
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="history-overlay">
          <div className="history-content">
            <p>{t("todo.historyTitle")}</p>
            <div className="completed-tasks">
              {completedTasks.length === 0 ? (
                <p>{t("todo.noHistoryTasks")}</p>
              ) : (
                completedTasks.map((task, index) => (
                  <div key={index} className="completed-task">
                    <span>
                      <strong>{t("todo.task")}</strong>: {task.text}
                    </span>
                    <span>
                      <strong>{t("todo.completed")}</strong>:{" "}
                      {new Date(
                        task.completedDate || task.date,
                      ).toLocaleDateString("pl-PL")}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
          <div className="creating">
            <button
              onClick={() => setShowHistory(false)}
              className="create-button"
            >
              {t("todo.backToMyTodo")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyTODO;
